// src/components/PublicChat.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  fetchPublicChat,
  sendPublicChatMessage,
  deletePublicChatMessage,
  togglePinPublicChatMessage,
} from "../api";
import { useAuth } from "./AuthGate";

const formatTime = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
};

/**
 * PublicChat - Community chat for premium subscribers
 *
 * Props:
 *   variant: "compact" | "full" - Display mode
 *   maxHeight: number - Max height of messages area (default 400)
 *   defaultExpanded: boolean - Start expanded (default false)
 */
export default function PublicChat({
  variant = "compact",
  maxHeight = 400,
  defaultExpanded = false,
}) {
  const { profile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [isAnnouncement, setIsAnnouncement] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const isPremium = profile?.is_premium || profile?.is_admin;
  const isAdmin = profile?.is_admin;
  const userId = profile?.db_user_id;

  // Load messages
  const loadMessages = useCallback(async (beforeId = null) => {
    if (!isPremium) return;

    try {
      setLoading(true);
      const data = await fetchPublicChat({
        limit: 30,
        beforeId,
      });
      if (beforeId) {
        setMessages((prev) => [...prev, ...data.messages]);
      } else {
        setMessages(data.messages);
        setPinnedMessages(data.pinned || []);
      }
      setHasMore(data.has_more);
      setError(null);
    } catch (err) {
      console.error("Failed to load chat:", err);
      setError(err?.response?.data?.detail || "Failed to load chat");
    } finally {
      setLoading(false);
    }
  }, [isPremium]);

  useEffect(() => {
    if (isPremium && expanded) {
      loadMessages();
    }
  }, [isPremium, expanded, loadMessages]);

  // Auto-refresh every 30s when expanded
  useEffect(() => {
    if (!expanded || !isPremium) return;
    const interval = setInterval(() => loadMessages(), 30000);
    return () => clearInterval(interval);
  }, [expanded, isPremium, loadMessages]);

  // Send message
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      const msg = await sendPublicChatMessage({
        content: newMessage.trim(),
        fixtureId: null,
        messageType: isAnnouncement && isAdmin ? "announcement" : "message",
      });
      setMessages((prev) => [msg, ...prev]);
      setNewMessage("");
      setIsAnnouncement(false);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // Delete message
  const handleDelete = async (msgId) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await deletePublicChatMessage(msgId);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId ? { ...m, is_deleted: true, content: "[Message deleted]" } : m
        )
      );
      setPinnedMessages((prev) => prev.filter((m) => m.id !== msgId));
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to delete");
    }
  };

  // Pin/unpin message
  const handleTogglePin = async (msgId) => {
    try {
      await togglePinPublicChatMessage(msgId);
      // Refresh to get updated pin status
      loadMessages();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to pin/unpin");
    }
  };

  // Load more
  const handleLoadMore = () => {
    if (messages.length > 0 && hasMore) {
      const lastId = messages[messages.length - 1].id;
      loadMessages(lastId);
    }
  };

  if (!isPremium) {
    return (
      <div style={styles.lockedContainer}>
        <div style={styles.lockedIcon}>💬</div>
        <div style={styles.lockedText}>
          <strong>Premium Community Chat</strong>
          <span style={{ opacity: 0.7 }}>
            Upgrade to Premium to join the community chat
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header - always visible */}
      <div
        style={styles.header}
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
      >
        <div style={styles.headerLeft}>
          <span style={styles.headerIcon}>💬</span>
          <span style={styles.headerTitle}>Community Chat</span>
          <span style={styles.premiumBadge}>Premium</span>
          {pinnedMessages.length > 0 && (
            <span style={styles.pinnedBadge}>{pinnedMessages.length}</span>
          )}
        </div>
        <span style={styles.expandIcon}>{expanded ? "▼" : "▶"}</span>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={styles.content}>
          {/* Pinned messages */}
          {pinnedMessages.length > 0 && (
            <div style={styles.pinnedSection}>
              {pinnedMessages.map((msg) => (
                <div key={msg.id} style={styles.pinnedMessage}>
                  <span style={styles.pinnedIcon}>📌</span>
                  <div style={styles.pinnedContent}>
                    <strong>{msg.user_display_name || "User"}</strong>:{" "}
                    {msg.content}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Messages list */}
          <div style={{ ...styles.messagesList, maxHeight }}>
            {loading && messages.length === 0 ? (
              <div style={styles.loadingText}>Loading chat...</div>
            ) : messages.length === 0 ? (
              <div style={styles.emptyText}>
                No messages yet. Be the first to say something!
              </div>
            ) : (
              <>
                {hasMore && (
                  <button
                    onClick={handleLoadMore}
                    style={styles.loadMoreBtn}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Load older messages"}
                  </button>
                )}
                {/* Messages in reverse (oldest at top) */}
                {[...messages].reverse().map((msg) => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    isOwnMessage={msg.user_id === userId}
                    isAdmin={isAdmin}
                    onDelete={() => handleDelete(msg.id)}
                    onTogglePin={() => handleTogglePin(msg.id)}
                  />
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Error display */}
          {error && <div style={styles.error}>{error}</div>}

          {/* Input form */}
          <form onSubmit={handleSend} style={styles.inputForm}>
            {isAdmin && (
              <label style={styles.announcementToggle}>
                <input
                  type="checkbox"
                  checked={isAnnouncement}
                  onChange={(e) => setIsAnnouncement(e.target.checked)}
                />
                <span>📢 Announcement</span>
              </label>
            )}
            <div style={styles.inputRow}>
              <input
                ref={inputRef}
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={
                  isAnnouncement
                    ? "Post an announcement..."
                    : "Type a message..."
                }
                style={styles.input}
                disabled={sending}
                maxLength={2000}
              />
              <button
                type="submit"
                disabled={sending || !newMessage.trim()}
                style={{
                  ...styles.sendBtn,
                  opacity: sending || !newMessage.trim() ? 0.5 : 1,
                }}
              >
                {sending ? "..." : "Send"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// Individual message bubble
function MessageBubble({
  message,
  isOwnMessage,
  isAdmin,
  onDelete,
  onTogglePin,
}) {
  const canDelete = isOwnMessage || isAdmin;
  const canPin = isAdmin && !message.is_deleted;

  return (
    <div
      style={{
        ...styles.messageBubble,
        ...(message.is_admin ? styles.adminMessage : {}),
        ...(message.is_premium && !message.is_admin ? styles.premiumMessage : {}),
        ...(message.message_type === "announcement"
          ? styles.announcementMessage
          : {}),
      }}
    >
      <div style={styles.messageHeader}>
        <div style={styles.messageAuthor}>
          {message.user_avatar_url && (
            <img
              src={message.user_avatar_url}
              alt=""
              style={styles.avatar}
              onError={(e) => (e.target.style.display = "none")}
            />
          )}
          <span style={styles.authorName}>
            {message.user_display_name || "User"}
            {message.is_admin && (
              <span style={styles.adminBadge}>Admin</span>
            )}
            {message.is_premium && !message.is_admin && (
              <span style={styles.premiumUserBadge}>Premium</span>
            )}
          </span>
          <span style={styles.messageTime}>
            {formatTime(message.created_at)}
            {message.edited_at && " (edited)"}
          </span>
        </div>
        {canDelete && !message.is_deleted && (
          <div style={styles.messageActions}>
            {canPin && (
              <button
                onClick={onTogglePin}
                style={styles.actionBtn}
                title={message.is_pinned ? "Unpin" : "Pin"}
              >
                {message.is_pinned ? "📌" : "📍"}
              </button>
            )}
            <button onClick={onDelete} style={styles.actionBtn} title="Delete">
              🗑️
            </button>
          </div>
        )}
      </div>
      <div
        style={{
          ...styles.messageContent,
          ...(message.is_deleted ? styles.deletedContent : {}),
        }}
      >
        {message.message_type === "announcement" && (
          <span style={styles.announcementPrefix}>📢 </span>
        )}
        {message.content}
        {message.fixture_label && (
          <span style={styles.fixtureTag}> ⚽ {message.fixture_label}</span>
        )}
      </div>
    </div>
  );
}

// Styles
const styles = {
  container: {
    background: "rgba(255, 255, 255, 0.02)",
    borderRadius: 12,
    border: "1px solid rgba(255, 255, 255, 0.08)",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    cursor: "pointer",
    background: "rgba(255, 255, 255, 0.03)",
    userSelect: "none",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  headerIcon: {
    fontSize: 18,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 14,
  },
  premiumBadge: {
    fontSize: 10,
    padding: "2px 6px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    fontWeight: 600,
  },
  pinnedBadge: {
    fontSize: 11,
    padding: "2px 6px",
    borderRadius: 999,
    background: "rgba(251, 191, 36, 0.15)",
    color: "#FBBF24",
  },
  expandIcon: {
    opacity: 0.5,
    fontSize: 12,
  },
  content: {
    borderTop: "1px solid rgba(255, 255, 255, 0.06)",
  },
  pinnedSection: {
    padding: "8px 16px",
    background: "rgba(251, 191, 36, 0.05)",
    borderBottom: "1px solid rgba(251, 191, 36, 0.15)",
  },
  pinnedMessage: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    fontSize: 13,
    padding: "4px 0",
  },
  pinnedIcon: {
    flexShrink: 0,
  },
  pinnedContent: {
    lineHeight: 1.4,
  },
  messagesList: {
    overflowY: "auto",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  loadingText: {
    textAlign: "center",
    opacity: 0.6,
    padding: 20,
  },
  emptyText: {
    textAlign: "center",
    opacity: 0.5,
    padding: 20,
    fontSize: 13,
  },
  loadMoreBtn: {
    alignSelf: "center",
    padding: "6px 12px",
    fontSize: 12,
    borderRadius: 6,
    border: "1px solid rgba(255, 255, 255, 0.15)",
    background: "transparent",
    color: "inherit",
    cursor: "pointer",
    marginBottom: 8,
  },
  messageBubble: {
    padding: "10px 12px",
    borderRadius: 10,
    background: "rgba(255, 255, 255, 0.04)",
    fontSize: 14,
  },
  adminMessage: {
    background: "rgba(239, 68, 68, 0.08)",
    borderLeft: "3px solid #ef4444",
  },
  premiumMessage: {
    background: "rgba(102, 126, 234, 0.08)",
    borderLeft: "3px solid #667eea",
  },
  announcementMessage: {
    background: "rgba(251, 191, 36, 0.08)",
    borderLeft: "3px solid #FBBF24",
  },
  messageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 6,
  },
  messageAuthor: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
  },
  avatar: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    objectFit: "cover",
  },
  authorName: {
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  adminBadge: {
    fontSize: 10,
    padding: "1px 5px",
    borderRadius: 999,
    background: "#ef4444",
    color: "white",
    marginLeft: 4,
  },
  premiumUserBadge: {
    fontSize: 10,
    padding: "1px 5px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    marginLeft: 4,
  },
  messageTime: {
    opacity: 0.5,
    fontSize: 11,
  },
  messageActions: {
    display: "flex",
    gap: 4,
  },
  actionBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    padding: 2,
    opacity: 0.6,
  },
  messageContent: {
    lineHeight: 1.5,
    wordBreak: "break-word",
  },
  deletedContent: {
    opacity: 0.5,
    fontStyle: "italic",
  },
  announcementPrefix: {
    marginRight: 2,
  },
  fixtureTag: {
    display: "inline-block",
    marginLeft: 8,
    fontSize: 11,
    opacity: 0.7,
    background: "rgba(255, 255, 255, 0.1)",
    padding: "2px 6px",
    borderRadius: 4,
  },
  error: {
    padding: "8px 16px",
    background: "rgba(220, 38, 38, 0.1)",
    color: "#ef4444",
    fontSize: 13,
    borderTop: "1px solid rgba(220, 38, 38, 0.2)",
  },
  inputForm: {
    padding: 12,
    borderTop: "1px solid rgba(255, 255, 255, 0.06)",
    background: "rgba(0, 0, 0, 0.2)",
  },
  announcementToggle: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    marginBottom: 8,
    opacity: 0.8,
    cursor: "pointer",
  },
  inputRow: {
    display: "flex",
    gap: 8,
  },
  input: {
    flex: 1,
    padding: "10px 14px",
    borderRadius: 8,
    border: "1px solid rgba(255, 255, 255, 0.1)",
    background: "rgba(255, 255, 255, 0.05)",
    color: "inherit",
    fontSize: 14,
    outline: "none",
  },
  sendBtn: {
    padding: "10px 18px",
    borderRadius: 8,
    border: "none",
    background: "#667eea",
    color: "white",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
  },
  // Locked state
  lockedContainer: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 16,
    background: "rgba(255, 255, 255, 0.02)",
    borderRadius: 12,
    border: "1px solid rgba(255, 255, 255, 0.08)",
  },
  lockedIcon: {
    fontSize: 24,
    opacity: 0.6,
  },
  lockedText: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 13,
  },
};
